// @givmo/board — where a director lands: which boards they serve on, and what's coming up.
//
// `GET /board-director/organizations/me` lists every board this ACCOUNT holds a claimed seat on,
// across organizations and both classes (CBG-SEC2, contract §6): a director serving three boards is
// the product's premise ("one identity across boards with per-board membership", plan §1.7). It joins
// seats by account and never by an address, so nobody can make a seat appear here by recording an
// email. A directory entry opens nothing by itself: each board's pages re-run the seam.
//
// ══ THE UPCOMING LIST KEEPS A DIRECTORY'S RESTRAINT ═════════════════════════════════════════════
//
// `GET /board-director/organizations/upcoming-meetings` spans the same boards and carries only which
// board, which seat, when, and this seat's own answer. ⚠ Deliberately no title, no location and no
// agenda — a meeting's title is CONTENT, and it is read on the board's own pages. A board that asks
// for a confirmation before anything opens has its meetings left out while the window is shut (the
// backend's collection rule), so this list never needs to hold back.
//
// ══ A YEAR'S DISCLOSURE WAITING ON THE PERSON (CBG-YR2-WEB2) ═══════════════════════════════════
//
// Each board's attention read names a year's conflict-of-interest disclosure sent to this person, open
// and not yet answered (`coi_disclosure`; the backend withholds the kind while the year is dark). It is
// one more row in this list, "Your 2027 conflict-of-interest disclosure · due Jan 29", leading to the
// year's card on that board's Conflicts of interest. Only the board and the due date: never a
// question, an answer or anyone else.
//
// ══ A CLOSED SESSION'S MINUTES TO SIGN TO APPROVE (CBG-RC10) ════════════════════════════════════
//
// The same read names a closed session's minutes this person was asked to sign to approve, as one of
// the directors who were in it (`minutes_approval_signature`; withheld while the signing window is
// shut, since whether someone was in a closed session is closed-session content). One more row:
// "Sign to approve the minutes of the closed session held during the February board meeting of
// February 11, 2027 (A conflict-of-interest matter)", leading to those minutes. Public facts only.
//
// ══ THE BOARD'S SELF-ASSESSMENT (CBG-YR4-ASSESS-WEB) ════════════════════════════════════════════
//
// The same read names the board's open self-assessment while this person was asked and hasn't
// answered in Board or on paper (`self_assessment`; withheld while the backend's flag is off). One
// more row, "Your board’s 2027 self-assessment · due Feb 26", leading to the person's own page. Its
// name and the date the board set only: never a question, an answer, a count or anyone else.
//
// ══ A COPY OF THE RETURN TO READ (CBG-YR5-DIST) ═════════════════════════════════════════════════
//
// The same read names a copy of the organization's return provided to this person that they haven't
// opened, been handed on paper or recorded reading (`return_to_read`; withheld while the backend's
// flag is off). One more row, "2026 Form 990 · provided Apr 1", leading to the person's own copy page,
// with NO badge (frame p5): reading it is theirs to choose, and nothing waits on them.
//
// ══ ONE SEAT LANDS ON ITS BOARD'S PAGE (CBG-FIX109) ═════════════════════════════════════════════
//
// Jonathan, 2026-10-17 20:48 PDT: "you want to have the homepage be a selector for the different
// boards someone is on? even though 99% of our users will only be on one board?" So once `/me` names
// one seat that opens something (`landingSeat`), this page replaces itself with that seat's page,
// its meetings with "Waiting on you" on top, showing only its loading card meanwhile so no list of
// one shows. Sign-in, joining and every email that links here land through it. This list is for
// two or more seats; a seat on no committee now opens nothing, so it stays here, its card saying so.
//
// ══ MINUTES WAITING WHILE THE SIGNING WINDOW IS SHUT (CBG-FIX74, frame m1) ══════════════════════
//
// The ask's email (CBG-RC10-MAIL) tells a director "The {Org} board has minutes waiting for your
// signature." and links here, but the RC10 row above is withheld while their window is shut. So each
// board's read carries one window-neutral fact, and where that board's read names no minutes (a read
// made while the window was shut) the list says the email's own words under the board's name,
// "Minutes waiting for your signature", with the row's usual Open and no badge — never the session,
// its meeting, its date or a count. The read decides, never `/me` (`director-waiting.ts`). Open
// brings the confirmation up by itself and goes on to the minutes (`minutes-waiting-open.tsx`).
//
// ══ A WRITTEN CONSENT WAITING FOR THEIR SIGNATURE (CBG-FIX107, frame m1) ════════════════════════
//
// The consent's circulation email links here too. One row per board says a consent waits, in the
// email's words and nothing of the consent (no title, text or date), the same open or shut; one
// committee's is named by its committee, as its meetings are. Open goes straight to the consent, or
// to Written consents when several wait.
//
// ══ EVERY ROW KEEPS ITS ACT AT THE ROW'S END ═══════════════════════════════════════════════════
//
// Every row in "Coming up" is the house's row (`coming-up-row.tsx`): at 1440 the RC10 row's long
// sentence used to push its badge and Open onto a line of their own.

import { useEffect } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { OrgBoardDirectorAttentionOut } from '@givmo/api-client';
import { Badge, Button, Card, CardBody, Skeleton, testIdProp } from '@givmo/design-system';
import { useNavigate } from '../../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardSearch,
  directorHomeTitle,
  holdsOnlyCommitteeSeats,
  landingSeat,
  seatCommitteeNames,
} from '../../director/director-board';
import { useBoardDirectorHost } from '../../director/director-host';
import { useDirectorMe } from '../../director/director-me';
import { isCommitteeOnly, type DirectorBoard } from '../../director/director-wire';
import { DirectorShell } from './director-shell';
import { ComingUpOpen, ComingUpRow } from './coming-up-row';
import { consentsLine, nothingWaits, waitingOn } from './director-waiting';
import { useMinutesWaitingOpen } from './minutes-waiting-open';
import { useMinuteClock } from '../../lib/minute-clock';
import { keptTogether, ownBoardCapacity } from '../roster/officer-roles';
import { officerRolesL3Words as l3 } from '../roster/officer-roles-l3-words';
import { termsWords as tw } from '../roster/terms-words';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { displayDate, todayAsDateInput } from '../../lib/display';
import { shortDay } from '../conflicts/coi-decode';
import { coiWords } from '../conflicts/coi-words';
import { onboardingWords as gs } from '../onboarding/onboarding-words';
import { returnWords as rw } from '../returns/return-words';
import { assessmentWords as sa } from '../evaluations/assessment-words';
import { minutesSignaturesWords as rc10 } from '../minutes/minutes-signatures-words';
import { closedMinutesOfTheMeeting } from '../minutes/minutes-rule-words';
import { meetingDayOf } from '../in-meeting/in-meeting-decode';
import {
  directorIntentLabel,
  displayMeetingStart,
  meetingCommitteeName,
  meetingHasStarted,
  meetingTypeLabel,
} from '../meetings/meetings-decode';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';

/**
 * The person's own term on a board (CBG-YR3-TERMS, frame t8): the end recorded for their seat — the
 * directory entry's dates, the current term's echo — or, once it has passed while they still serve,
 * the approved holdover words. Never a count against a limit, and nothing in their attention list:
 * there is nothing for them to do (backend `board-terms.md` §11.1).
 */
export function ownTermLine(
  board: { term_end_on: string | null; status: string },
  today: string,
): string | null {
  const end = board.term_end_on;
  if (end === null || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return null;
  return end < today && board.status === 'active'
    ? keptTogether(l3.termEndedStillServing(displayDate(end)))
    : tw.endsOn(displayDate(end));
}

/**
 * A waiting row's first line on the list: the board's name, or, when none can be read, whose seat it
 * is (CBG-FIX104, row 4.4: a committee's for someone who isn't on the board).
 */
function boardName(board: DirectorBoard): string {
  return (
    board.organization_name?.trim() ||
    (isCommitteeOnly(board) ? co.unnamedCommittee : 'A board you serve on')
  );
}

export function DirectorBoardsPage() {
  const navigate = useNavigate();
  const operations = useBoardDirectorOperations();
  const { account } = useBoardDirectorHost();
  const meQuery = useDirectorMe();
  // CBG-FIX109: the one seat this account lands on, whose page replaces this one.
  const landing = meQuery.isSuccess ? landingSeat(meQuery.data) : null;
  const landingOrg = landing?.org_ref ?? null;
  useEffect(() => {
    if (landingOrg === null) return;
    void navigate({
      to: BOARD_DIRECTOR_PAGES.meetings,
      search: directorBoardSearch(landingOrg),
      replace: true,
    });
  }, [landingOrg, navigate]);

  const upcomingQuery = useQuery({
    queryKey: ['board-director-upcoming'],
    // Read beside `/me`, as before; never again once home is the one seat's page.
    enabled: account !== null && landingOrg === null,
    queryFn: async () => {
      const { data } = await operations.directorListBoardDirectorUpcomingMeetings({
        throwOnError: true,
      });
      return Array.isArray(data) ? data : [];
    },
  });

  const boards = meQuery.data?.boards ?? [];
  const upcoming = upcomingQuery.data ?? [];
  // CBG-YR6-OUTSIDE (frame o7): an account whose every seat is a committee's, for someone who isn't
  // on that board, is home to "Your committees"; one that also sits on a board somewhere keeps "Your
  // boards" and says both.
  const committeeOnlyCount = boards.filter(isCommitteeOnly).length;
  const allCommitteeOnly = holdsOnlyCommitteeSeats(meQuery.data);
  // CBG-FIX104 (row 4.3): an account on a board and on a committee elsewhere says both. So does
  // home until `/me` has answered, or when it can't (CBG-FIX106, row 4.7's rule), and for an
  // account holding no seat (review-233's F3): it doesn't know whose seats they are or were, so
  // each line is the approved one true of both.
  const mixed =
    !meQuery.isSuccess || boards.length === 0 || (!allCommitteeOnly && committeeOnlyCount > 0);
  const now = useMinuteClock(null);

  // Each board's attention read, under the key its own pages read it by.
  const attentionQueries = useQueries({
    queries: boards.map((board) => ({
      queryKey: ['board-director-attention', board.org_ref],
      enabled: account !== null,
      retry: false,
      queryFn: async (): Promise<OrgBoardDirectorAttentionOut> => {
        const { data } = await operations.directorGetBoardDirectorAttention({
          path: { org_ref: board.org_ref },
          throwOnError: true,
        });
        return data;
      },
    })),
  });
  // What waits on the person on each board, read once per board (`director-waiting.ts`) and listed
  // kind by kind across the boards, in the list's order.
  const waitingBy = boards.map((board, index) => ({
    board,
    waiting: waitingOn(attentionQueries[index]?.data),
  }));
  // CBG-FIX74: minutes waiting for the person's signature, said where the board's read names none.
  const minutesWhileShut = waitingBy
    .filter(({ waiting }) => waiting.minutesWhileShut)
    .map(({ board }) => board);
  // CBG-RC10: a closed session's minutes this person was asked to sign to approve.
  const waitingApprovals = waitingBy.flatMap(({ board, waiting }) =>
    waiting.approvals.map((item) => ({ board, item })),
  );
  // CBG-FIX107: written consents waiting for the person's signature, one row per board.
  const waitingConsents = waitingBy
    .filter(({ waiting }) => waiting.consents.length > 0)
    .map(({ board, waiting }) => ({ board, consents: waiting.consents }));
  // CBG-YR4-START (frame g4): the person's getting-started list, while it has something to read —
  // a count of their own items, never a line about anyone else's.
  const waitingStarts = waitingBy
    .filter(({ waiting }) => waiting.toRead > 0)
    .map(({ board, waiting }) => ({ board, toRead: waiting.toRead }));
  // CBG-YR4-ASSESS-WEB: the board's open self-assessment, not yet answered by this person — its name
  // and the date the board set, never a question, an answer or anyone else.
  const waitingAssessments = waitingBy.flatMap(({ board, waiting }) =>
    waiting.assessments.map((item) => ({ board, item })),
  );
  const waitingYears = waitingBy.flatMap(({ board, waiting }) =>
    waiting.years.map((year) => ({ board, year })),
  );
  // CBG-YR5-DIST: a copy of the return provided to this person, not yet opened or read.
  const waitingReturns = waitingBy.flatMap(({ board, waiting }) =>
    waiting.returns.map((item) => ({ board, item })),
  );
  const minutesOpen = useMinutesWaitingOpen(account?.displayName ?? account?.email ?? undefined);

  // CBG-FIX109: until `/me` answers, and on the way to the one seat's page, the loading card alone,
  // under the title and line true of both (CBG-FIX106, row 4.7's rule), so no list of one shows.
  if (meQuery.isPending || landingOrg !== null) {
    return (
      <DirectorShell
        title={directorHomeTitle({ isSuccess: false })}
        description={co.homeDescriptionMixed}
        identity={account?.displayName ?? account?.email ?? undefined}
        testId="page-director-boards"
      >
        <div className="flex flex-col gap-12" data-testid="director-boards-loading">
          <Skeleton className="h-84 w-full rounded-lg" />
        </div>
      </DirectorShell>
    );
  }

  return (
    <DirectorShell
      // CBG-FIX106 (row 4.7, approved 2026-10-17): until `/me` has answered, when it can't, and for
      // an account holding no seat, home doesn't know whose seats they are, so its title is true of
      // both — the title the way home and the account menu say too.
      title={directorHomeTitle(meQuery)}
      // Removed before finishing (frame o7): "The committees you serve on." under "Your committees"
      // said the title again.
      description={
        allCommitteeOnly ? undefined : mixed ? co.homeDescriptionMixed : 'The boards you serve on.'
      }
      identity={account?.displayName ?? account?.email ?? undefined}
      testId="page-director-boards"
    >
      {meQuery.isError ? (
        <Card testId="director-boards-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {/* CBG-FIX104 (row 4.4, approved 2026-10-17): true of every account, from home's own
                  "The boards and committees you serve on." */}
              We couldn’t load the boards and committees you serve on just now.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void meQuery.refetch()}
              testId="director-boards-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {boards.map((board) => (
        <Card key={board.board_member_id} testId={`director-board-${board.org_ref}`}>
          <CardBody className="flex flex-wrap items-center justify-between gap-16">
            <div className="flex min-w-0 flex-col gap-4">
              <span className="truncate font-sans text-s16 font-w600 tracking-givmo text-text-strong">
                {/* CBG-FIX104 (row 4.4): with no organization's name, a committee seat says so. */}
                {board.organization_name?.trim() ||
                  (isCommitteeOnly(board) ? co.unnamedSeat : 'A board you serve on')}
              </span>
              <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
                {/* The person's facts on this board (CBG-YR3-ROLES layer 1), and their own term.
                    CBG-YR6-OUTSIDE (frame o7): a seat for someone who isn't on the board is its
                    committees, each kept whole, the chair marked — no director fact, no term. */}
                {isCommitteeOnly(board)
                  ? seatCommitteeNames(board).length > 0
                    ? (board.committees ?? [])
                        .map((row) =>
                          keptTogether(cm.committeeWithRole(row.name, row.role === 'chair')),
                        )
                        .join(' · ')
                    : co.onNoCommittee
                  : [ownBoardCapacity(board), ownTermLine(board, todayAsDateInput())]
                      .filter((part): part is string => part !== null && part.length > 0)
                      .join(' · ')}
              </span>
              {/* CBG-YR6-MEET (frame c8): the committees this person serves on, as recorded (a
                  committee-only seat's line above already is). */}
              {(board.committees ?? []).length > 0 && !isCommitteeOnly(board) ? (
                <span
                  className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
                  data-testid={`director-board-committees-${board.org_ref}`}
                >
                  {/* Each committee kept whole: a phone breaks the line between them. */}
                  {cm.yourCommittees(
                    (board.committees ?? []).map((row) =>
                      keptTogether(cm.committeeWithRole(row.name, row.role === 'chair')),
                    ),
                  )}
                </span>
              ) : null}
            </div>
            {/* A committee-only seat on no committee now opens nothing: no button. */}
            {isCommitteeOnly(board) && seatCommitteeNames(board).length === 0 ? null : (
              <Button
                appearance="saas"
                tone="primary"
                onClick={() =>
                  void navigate({
                    to: BOARD_DIRECTOR_PAGES.meetings,
                    search: directorBoardSearch(board.org_ref),
                  })
                }
                testId={`director-open-${board.org_ref}`}
              >
                {isCommitteeOnly(board)
                  ? co.openButton(seatCommitteeNames(board))
                  : 'Open this board'}
              </Button>
            )}
          </CardBody>
        </Card>
      ))}

      {meQuery.isSuccess && boards.length === 0 ? (
        <p
          className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp('director-boards-empty')}
        >
          {/* CBG-FIX111 (its copy page's row 1.2, approved 2026-10-18 17:23 PDT): said to every
              account holding no seat, a committee's invitee or a seat a refusal took away among
              them, so it names neither a board nor "yet", and the email as the committees' mails
              do. */}
          You don’t have a seat on a board or committee with this account. To join, open the
          invitation email you were sent.
        </p>
      ) : null}

      {/* ── What is coming up, across every board ─────────────────────────────────────────────── */}
      <Card testId="director-upcoming">
        <CardBody className="flex flex-col gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              Coming up
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {/* Says WHY there is no title here, so its absence reads as a boundary rather than as
                  missing data. CBG-YR6-OUTSIDE (frame o7): an account of committees alone says
                  committees. */}
              {allCommitteeOnly
                ? co.comingUpLead
                : mixed
                  ? co.comingUpLeadMixed
                  : 'Dates from every board you serve on. What each meeting is about is on its board.'}
            </p>
          </div>

          {upcomingQuery.isPending ? (
            <Skeleton className="h-48 w-full rounded-lg" testId="director-upcoming-loading" />
          ) : null}

          {upcomingQuery.isError ? (
            <div className="flex flex-col items-start gap-10" data-testid="director-upcoming-error">
              <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                We couldn’t load what’s coming up.
              </p>
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => void upcomingQuery.refetch()}
                testId="director-upcoming-retry"
              >
                Try again
              </Button>
            </div>
          ) : null}

          {/* The empty line waits on everything the list can show, the minutes line included. */}
          {upcomingQuery.isSuccess &&
          upcoming.length === 0 &&
          waitingBy.every(({ waiting }) => nothingWaits(waiting)) ? (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp('director-upcoming-empty')}
            >
              {/* CBG-FIX104 (row 4.3): said for the seats the account holds. */}
              {allCommitteeOnly
                ? co.meetingsEmpty
                : mixed
                  ? co.nothingScheduledMixed
                  : 'Nothing is scheduled on any of your boards right now.'}
            </p>
          ) : null}

          {/* Every row is the house's row (`coming-up-row.tsx`): its act stays at its end. */}
          <ul className="flex flex-col gap-12">
            {/* CBG-FIX74 (frame m1): where the board's read names no minutes, the ask email's own
                words in the RC10 row's place, under the board's name, with the row's usual Open and
                no badge: the words already say it waits on them. */}
            {minutesWhileShut.map((board) => (
              <ComingUpRow
                key={`${board.org_ref}:minutes-waiting`}
                heading={boardName(board)}
                line={rc10.minutesWaiting}
                act={
                  <ComingUpOpen
                    loading={minutesOpen.working && minutesOpen.opening === board.org_ref}
                    onClick={() => minutesOpen.open(board.org_ref)}
                    testId={`director-upcoming-minutes-open-${board.org_ref}`}
                  />
                }
                testId={`director-upcoming-minutes-${board.org_ref}`}
              />
            ))}
            {waitingApprovals.map(({ board, item }) => (
              <ComingUpRow
                key={`${board.org_ref}:${item.recordId}`}
                heading={boardName(board)}
                line={rc10.attentionRow(
                  closedMinutesOfTheMeeting(
                    item.meetingTitle,
                    meetingDayOf({
                      starts_at: item.meetingStartsAt,
                      time_zone: item.meetingTimeZone,
                    })?.day ?? null,
                    item.purposeLabel,
                    { keepDateTogether: true },
                  ),
                )}
                note={item.signedEarlierVersion ? rc10.signTheCurrentVersion : undefined}
                badge={<Badge tone="warning">{coiWords.waitingOnYou}</Badge>}
                act={
                  <ComingUpOpen
                    onClick={() =>
                      void navigate({
                        to: BOARD_DIRECTOR_PAGES.record,
                        params: { recordId: item.recordId },
                        search: directorBoardSearch(board.org_ref),
                      })
                    }
                    testId={`director-upcoming-approval-open-${item.recordId}`}
                  />
                }
                testId={`director-upcoming-approval-${item.recordId}`}
              />
            ))}
            {/* CBG-FIX107 (frame m1): one row per board in the circulation email's words, the same
                open or shut, with no badge and nothing of the consent — no title, text or date. */}
            {waitingConsents.map(({ board, consents }) => (
              <ComingUpRow
                key={`${board.org_ref}:consents`}
                heading={boardName(board)}
                line={consentsLine(consents, { committeeSaid: true })}
                act={
                  <ComingUpOpen
                    onClick={() => {
                      const [only, ...more] = consents;
                      void (only !== undefined && more.length === 0
                        ? navigate({
                            to: BOARD_DIRECTOR_PAGES.consent,
                            params: { consentId: only.consentId },
                            search: directorBoardSearch(board.org_ref),
                          })
                        : navigate({
                            to: BOARD_DIRECTOR_PAGES.consents,
                            search: directorBoardSearch(board.org_ref),
                          }));
                    }}
                    testId={`director-upcoming-consents-open-${board.org_ref}`}
                  />
                }
                testId={`director-upcoming-consents-${board.org_ref}`}
              />
            ))}
            {waitingStarts.map(({ board, toRead }) => (
              <ComingUpRow
                key={`${board.org_ref}:getting-started`}
                heading={boardName(board)}
                line={gs.attentionRow(toRead)}
                badge={<Badge tone="warning">{coiWords.waitingOnYou}</Badge>}
                act={
                  <ComingUpOpen
                    onClick={() =>
                      void navigate({
                        to: BOARD_DIRECTOR_PAGES.gettingStarted,
                        search: directorBoardSearch(board.org_ref),
                      })
                    }
                    testId={`director-upcoming-start-open-${board.org_ref}`}
                  />
                }
                testId={`director-upcoming-start-${board.org_ref}`}
              />
            ))}
            {waitingAssessments.map(({ board, item }) => (
              <ComingUpRow
                key={`${board.org_ref}:${item.assessmentId}`}
                heading={boardName(board)}
                line={sa.attentionRow(item.label, shortDay(item.dueOn))}
                badge={<Badge tone="warning">{coiWords.waitingOnYou}</Badge>}
                act={
                  <ComingUpOpen
                    onClick={() =>
                      void navigate({
                        to: BOARD_DIRECTOR_PAGES.selfAssessment,
                        search: {
                          ...directorBoardSearch(board.org_ref),
                          assessment: item.assessmentId,
                        },
                      })
                    }
                    testId={`director-upcoming-assessment-open-${item.assessmentId}`}
                  />
                }
                testId={`director-upcoming-assessment-${item.assessmentId}`}
              />
            ))}
            {waitingYears.map(({ board, year }) => (
              <ComingUpRow
                key={`${board.org_ref}:${year.cycleId}`}
                heading={boardName(board)}
                line={
                  year.joinedAfterClose
                    ? coiWords.attentionRowJoinedAfterClose(year.label)
                    : coiWords.attentionRow(year.label, shortDay(year.dueOn))
                }
                // The list's own word for what waits on the person, as its meeting rows say it.
                badge={<Badge tone="warning">{coiWords.waitingOnYou}</Badge>}
                act={
                  <ComingUpOpen
                    onClick={() =>
                      void navigate({
                        to: BOARD_DIRECTOR_PAGES.disclosures,
                        search: directorBoardSearch(board.org_ref),
                      })
                    }
                    testId={`director-upcoming-coi-open-${year.cycleId}`}
                  />
                }
                testId={`director-upcoming-coi-${year.cycleId}`}
              />
            ))}
            {waitingReturns.map(({ board, item }) => (
              <ComingUpRow
                key={`${board.org_ref}:${item.recordId}`}
                heading={boardName(board)}
                line={rw.attentionRow(item.namedYear, item.form, shortDay(item.providedOn))}
                act={
                  <ComingUpOpen
                    label={rw.open}
                    onClick={() =>
                      void navigate({
                        to: BOARD_DIRECTOR_PAGES.returnCopy,
                        params: { recordId: item.recordId },
                        search: directorBoardSearch(board.org_ref),
                      })
                    }
                    testId={`director-upcoming-return-open-${item.recordId}`}
                  />
                }
                testId={`director-upcoming-return-${item.recordId}`}
              />
            ))}
            {upcoming.map((meeting) => (
              <ComingUpRow
                key={meeting.meeting_id}
                // CBG-FIX104 (row 4.4): a committee's meeting, with no organization's name.
                heading={
                  meeting.organization_name?.trim() ||
                  (meetingCommitteeName(meeting) !== null
                    ? co.unnamedCommittee
                    : 'A board you serve on')
                }
                // CBG-YR6-MEET (frame c8): a committee's meeting is named by its committee, never
                // by the meeting's title ("Finance Committee · Apr 1, 2027 …").
                line={`${meetingCommitteeName(meeting) ?? meetingTypeLabel(meeting.meeting_type)} · ${displayMeetingStart(
                  {
                    starts_at: meeting.starts_at,
                    time_zone: meeting.time_zone,
                  },
                )}`}
                // A page left open across a meeting's start shows no answer, and asks for none, from
                // then on (CBG-FIX25); the read itself lists only meetings still ahead.
                badge={
                  meetingHasStarted(meeting.starts_at, now) ? null : meeting.my_intent ? (
                    <Badge tone="info">{directorIntentLabel(meeting.my_intent)}</Badge>
                  ) : (
                    <Badge tone="warning">Waiting on you</Badge>
                  )
                }
                act={
                  <ComingUpOpen
                    onClick={() =>
                      void navigate({
                        to: BOARD_DIRECTOR_PAGES.meeting,
                        params: { meetingId: meeting.meeting_id },
                        search: directorBoardSearch(meeting.org_ref),
                      })
                    }
                    testId={`director-upcoming-open-${meeting.meeting_id}`}
                  />
                }
                testId={`director-upcoming-${meeting.meeting_id}`}
              />
            ))}
          </ul>
        </CardBody>
      </Card>
      {minutesOpen.dialog}
      {minutesOpen.offer}
    </DirectorShell>
  );
}
